import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  GraduationCap,
  UserPlus,
  Search,
  MoreVertical,
  BookOpen,
  Layers,
  CreditCard,
  CheckCircle2,
  Clock,
  Globe,
  Building,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Award,
  ShieldCheck,
  ShieldAlert,
  ExternalLink,
  ChevronRight,
  Eye,
  Trash2,
  Lock,
  Unlock,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import { AdminEnrollmentItem, AdminEnrollmentKpis } from "../../services/enrollmentService";
import {
  useAdminEnrollmentsQuery,
  useUpdateAdminEnrollmentMutation,
  useDeleteAdminEnrollmentMutation,
} from "../../hooks/queries/useEnrollmentAdminQueries";
import { useToast } from "../../hooks/otherHooks/useToast";
import ManualEnrollmentModal from "./components/ManualEnrollmentModal";
import ConfirmTuitionModal from "./components/ConfirmTuitionModal";
import EnrollmentDetailModal from "./components/EnrollmentDetailModal";

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div
    className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}
  >
    {children}
  </div>
);

const SuperAdminEnrollments: React.FC = () => {
  const { addToast } = useToast();

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [deliveryMode, setDeliveryMode] = useState<string>("all");
  const [curriculumType, setCurriculumType] = useState<string>("all");
  const [paymentStatus, setPaymentStatus] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Active row dropdown state
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Modals state
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [tuitionModalItem, setTuitionModalItem] = useState<AdminEnrollmentItem | null>(null);
  const [detailModalItem, setDetailModalItem] = useState<AdminEnrollmentItem | null>(null);

  // Debounced search (350ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Structured query params for TanStack Query
  const queryParams = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      delivery_mode: deliveryMode !== "all" ? deliveryMode : undefined,
      curriculum_type: curriculumType !== "all" ? curriculumType : undefined,
      payment_status: paymentStatus !== "all" ? paymentStatus : undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
    }),
    [debouncedSearch, deliveryMode, curriculumType, paymentStatus, statusFilter]
  );

  // Query enrollments with 3-minute cost-saving cache
  const {
    data: responseData,
    isLoading: isQueryLoading,
    isFetching: isQueryFetching,
    refetch,
  } = useAdminEnrollmentsQuery(queryParams);

  const enrollments = useMemo<AdminEnrollmentItem[]>(
    () => (Array.isArray(responseData?.enrollments) ? responseData.enrollments : []),
    [responseData?.enrollments]
  );

  const kpis = useMemo<AdminEnrollmentKpis>(
    () =>
      responseData?.kpis || {
        total_enrollments: 0,
        online_count: 0,
        onsite_count: 0,
        paid_count: 0,
        pending_payment_count: 0,
        completed_count: 0,
        total_tuition_volume: 0,
      },
    [responseData?.kpis]
  );

  const loading = isQueryLoading && !responseData;

  // Mutations
  const { mutateAsync: updateEnrollmentMutation } = useUpdateAdminEnrollmentMutation();
  const { mutateAsync: deleteEnrollmentMutation } = useDeleteAdminEnrollmentMutation();

  // Outside click listener for action dropdowns
  useEffect(() => {
    const handlePointerDownOutside = (event: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handlePointerDownOutside);
    document.addEventListener("touchstart", handlePointerDownOutside);
    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("touchstart", handlePointerDownOutside);
    };
  }, []);

  const handleToggleAccess = async (item: AdminEnrollmentItem) => {
    try {
      await updateEnrollmentMutation({
        id: item.id,
        mode: item.delivery_mode,
        data: { is_enrolled: !item.is_enrolled },
      });
      addToast(
        `Curriculum access ${!item.is_enrolled ? "activated" : "suspended"} for student.`,
        "success"
      );
      setActiveDropdown(null);
    } catch {
      addToast("Failed to toggle curriculum access.", "error");
    }
  };

  const handleToggleGraduation = async (item: AdminEnrollmentItem) => {
    try {
      await updateEnrollmentMutation({
        id: item.id,
        mode: item.delivery_mode,
        data: {
          is_completed: !item.is_completed,
          progress_percent: !item.is_completed ? 100 : item.progress_percent,
        },
      });
      addToast(
        `Student marked as ${!item.is_completed ? "Graduated" : "In Progress"}.`,
        "success"
      );
      setActiveDropdown(null);
    } catch {
      addToast("Failed to update graduation state.", "error");
    }
  };

  const handleDeleteEnrollment = async (item: AdminEnrollmentItem) => {
    if (window.confirm(`Are you sure you want to remove the enrollment record for ${item.student_details?.name || item.student_details?.email}?`)) {
      try {
        await deleteEnrollmentMutation({ id: item.id, mode: item.delivery_mode });
        addToast("Enrollment admission record deleted successfully.", "success");
        setActiveDropdown(null);
      } catch {
        addToast("Failed to delete enrollment record.", "error");
      }
    }
  };

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      {/* Header & Command Ribbon */}
      <div className="cht-flex cht-flex-col md:cht-flex-row md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2">
            <span className="cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest">
              Admissions Governance
            </span>
            <span className="cht-text-gray-400 cht-text-xs">•</span>
            <span className="cht-text-xs cht-font-bold cht-text-gray-500">
              Academic Operations Subsystem
            </span>
          </div>
          <h1 className="cht-text-3xl cht-font-black cht-text-gray-900 cht-tracking-tight cht-mt-1">
            Student Admissions & Enrollment
          </h1>
          <p className="cht-text-xs cht-text-gray-500 cht-font-medium cht-mt-0.5">
            Centralized platform admissions authority, offline tuition settlement, and learning progression telemetry
          </p>
        </div>

        <div className="cht-flex cht-items-center cht-gap-3">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isQueryFetching}
            className="cht-rounded-xl cht-text-xs cht-font-bold cht-border-gray-200 cht-text-gray-700 hover:cht-bg-gray-50"
            leftIcon={
              <RefreshCw
                size={14}
                className={isQueryFetching ? "cht-animate-spin cht-text-[#3e57da]" : ""}
              />
            }
          >
            Sync
          </Button>

          <Button
            variant="primary"
            onClick={() => setIsManualModalOpen(true)}
            className="cht-bg-[#3e57da] hover:cht-bg-[#2d41a8] cht-rounded-xl cht-text-xs cht-font-bold cht-px-5"
            leftIcon={<UserPlus size={16} />}
          >
            Direct Admissions Enrollment
          </Button>
        </div>
      </div>

      {/* KPI Ribbon (5 GlassCards) */}
      <div className="cht-grid cht-grid-cols-2 sm:cht-grid-cols-3 lg:cht-grid-cols-5 cht-gap-4">
        {[
          {
            label: "Total Admissions",
            val: kpis.total_enrollments,
            sub: `${kpis.online_count} Online • ${kpis.onsite_count} Onsite`,
            icon: GraduationCap,
            color: "text-[#3e57da]",
            bg: "bg-[#3e57da]/10",
          },
          {
            label: "Online Learners",
            val: kpis.online_count,
            sub: "Full platform LMS telemetry",
            icon: Globe,
            color: "text-blue-600",
            bg: "bg-blue-50",
          },
          {
            label: "Onsite Cohorts",
            val: kpis.onsite_count,
            sub: "Physical campus bootcamp",
            icon: Building,
            color: "text-emerald-600",
            bg: "bg-emerald-50",
          },
          {
            label: "Tuition Volume",
            val: `₦${kpis.total_tuition_volume.toLocaleString()}`,
            sub: `${kpis.paid_count} settled • ${kpis.pending_payment_count} pending`,
            icon: CreditCard,
            color: "text-indigo-600",
            bg: "bg-indigo-50",
          },
          {
            label: "Graduated Students",
            val: kpis.completed_count,
            sub: "Eligible for certificate badge",
            icon: Award,
            color: "text-purple-600",
            bg: "bg-purple-50",
          },
        ].map((card, i) => (
          <GlassCard key={i} className="cht-p-5 cht-rounded-2xl cht-space-y-3">
            <div className="cht-flex cht-items-center cht-justify-between">
              <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                {card.label}
              </span>
              <div className={`cht-w-8 cht-h-8 cht-rounded-xl cht-flex cht-items-center cht-justify-center ${card.bg} ${card.color}`}>
                <card.icon size={16} />
              </div>
            </div>
            <div>
              <p className="cht-text-2xl cht-font-black cht-text-gray-900 cht-tracking-tight">{card.val}</p>
              <p className="cht-text-[10px] cht-text-gray-400 cht-font-medium cht-mt-0.5">{card.sub}</p>
            </div>
          </GlassCard>
        ))}
      </div>

      {/* Filter & Search Bar */}
      <GlassCard className="cht-p-5 cht-rounded-2xl cht-space-y-4">
        <div className="cht-flex cht-flex-col lg:cht-flex-row lg:cht-items-center cht-justify-between cht-gap-4">
          {/* Live Search */}
          <div className="cht-relative cht-flex-1">
            <Search
              size={16}
              className="cht-absolute cht-left-3.5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400"
            />
            <input
              type="text"
              placeholder="Search by student name, email, phone, or curriculum title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="cht-w-full cht-pl-10 cht-pr-4 cht-py-2.5 cht-rounded-xl cht-border cht-border-gray-200 cht-bg-white cht-text-xs cht-font-medium cht-text-gray-800 placeholder-gray-400 focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/20 focus:cht-border-[#3e57da]"
            />
          </div>

          {/* Delivery Mode Pills */}
          <div className="cht-flex cht-items-center cht-gap-1.5 cht-bg-gray-100/70 cht-p-1 cht-rounded-xl">
            {[
              { id: "all", label: "All Modes" },
              { id: "online", label: "Online" },
              { id: "onsite", label: "Onsite" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setDeliveryMode(tab.id)}
                className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-black cht-uppercase cht-tracking-wider cht-transition-all ${
                  deliveryMode === tab.id
                    ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                    : "cht-text-gray-500 hover:cht-text-gray-800"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Dropdown Filters */}
          <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
            <select
              value={curriculumType}
              onChange={(e) => setCurriculumType(e.target.value)}
              className="cht-px-3 cht-py-2 cht-rounded-xl cht-border cht-border-gray-200 cht-bg-white cht-text-xs cht-font-bold cht-text-gray-700"
            >
              <option value="all">All Curriculum</option>
              <option value="course">Courses Only</option>
              <option value="track">Career Tracks Only</option>
            </select>

            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="cht-px-3 cht-py-2 cht-rounded-xl cht-border cht-border-gray-200 cht-bg-white cht-text-xs cht-font-bold cht-text-gray-700"
            >
              <option value="all">All Tuition States</option>
              <option value="paid">Paid / Settled</option>
              <option value="unpaid">Pending Payment</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="cht-px-3 cht-py-2 cht-rounded-xl cht-border cht-border-gray-200 cht-bg-white cht-text-xs cht-font-bold cht-text-gray-700"
            >
              <option value="all">All Academic States</option>
              <option value="active">Active Access</option>
              <option value="completed">Graduated (100%)</option>
              <option value="pending">Pending Access</option>
            </select>
          </div>
        </div>
      </GlassCard>

      {/* Admissions Table (CoreDesign 56px rows, squircle cards) */}
      <GlassCard className="cht-rounded-3xl cht-overflow-hidden cht-border cht-border-gray-100 cht-min-h-[400px] cht-pb-24">
        <div className="cht-overflow-x-auto">
          <table className="cht-w-full cht-text-left cht-border-collapse">
            <thead>
              <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/50 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                <th className="cht-py-4 cht-px-6">Student Learner</th>
                <th className="cht-py-4 cht-px-6">Enrolled Curriculum</th>
                <th className="cht-py-4 cht-px-6">Tuition & Financials</th>
                <th className="cht-py-4 cht-px-6">Academic Progression</th>
                <th className="cht-py-4 cht-px-6">Access State</th>
                <th className="cht-py-4 cht-px-6 cht-text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="cht-divide-y cht-divide-gray-100/70 cht-text-xs">
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="cht-h-14 cht-animate-pulse">
                    <td className="cht-py-3 cht-px-6">
                      <div className="cht-flex cht-items-center cht-gap-3">
                        <div className="cht-w-9 cht-h-9 cht-rounded-full cht-bg-gray-200" />
                        <div className="cht-space-y-1">
                          <div className="cht-w-28 cht-h-3 cht-bg-gray-200 cht-rounded" />
                          <div className="cht-w-36 cht-h-2.5 cht-bg-gray-100 cht-rounded" />
                        </div>
                      </div>
                    </td>
                    <td className="cht-py-3 cht-px-6">
                      <div className="cht-w-40 cht-h-3 cht-bg-gray-200 cht-rounded" />
                    </td>
                    <td className="cht-py-3 cht-px-6">
                      <div className="cht-w-24 cht-h-3 cht-bg-gray-200 cht-rounded" />
                    </td>
                    <td className="cht-py-3 cht-px-6">
                      <div className="cht-w-24 cht-h-3 cht-bg-gray-200 cht-rounded" />
                    </td>
                    <td className="cht-py-3 cht-px-6">
                      <div className="cht-w-16 cht-h-4 cht-bg-gray-200 cht-rounded" />
                    </td>
                    <td className="cht-py-3 cht-px-6 cht-text-right">
                      <div className="cht-w-6 cht-h-6 cht-bg-gray-200 cht-rounded cht-ml-auto" />
                    </td>
                  </tr>
                ))
              ) : enrollments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="cht-py-16 cht-text-center">
                    <div className="cht-max-w-sm cht-mx-auto cht-space-y-3">
                      <div className="cht-w-12 cht-h-12 cht-rounded-2xl cht-bg-gray-100 cht-text-gray-400 cht-flex cht-items-center cht-justify-center cht-mx-auto">
                        <GraduationCap size={24} />
                      </div>
                      <h4 className="cht-text-sm cht-font-black cht-text-gray-900">
                        No student admissions found
                      </h4>
                      <p className="cht-text-xs cht-text-gray-400 cht-font-medium">
                        Try modifying search terms, delivery mode filters, or enroll a student manually.
                      </p>
                      <Button
                        variant="primary"
                        onClick={() => setIsManualModalOpen(true)}
                        className="cht-bg-[#3e57da] hover:cht-bg-[#2d41a8] cht-rounded-xl cht-text-xs cht-font-bold"
                        leftIcon={<UserPlus size={14} />}
                      >
                        Enroll Student Now
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                enrollments.map((item, index) => {
                  const isNearBottom = index >= enrollments.length - 2 && enrollments.length > 2;
                  return (
                    <tr
                      key={`${item.delivery_mode}-${item.id}`}
                      className="cht-h-14 hover:cht-bg-gray-50/80 cht-transition-colors group"
                    >
                      {/* Student Learner */}
                      <td className="cht-py-3 cht-px-6">
                        <div className="cht-flex cht-items-center cht-gap-3">
                          <img
                            src={
                              item.student_details?.photo_url ||
                              "https://cdn-icons-png.flaticon.com/512/147/147142.png"
                            }
                            alt={item.student_details?.name}
                            className="cht-w-9 cht-h-9 cht-rounded-full cht-object-cover cht-border cht-border-gray-200/80"
                          />
                          <div>
                            <div className="cht-flex cht-items-center cht-gap-2">
                              <span className="cht-font-black cht-text-gray-900">
                                {item.student_details?.name || item.student_details?.email}
                              </span>
                              <span
                                className={`cht-text-[9px] cht-font-black cht-uppercase cht-px-1.5 cht-py-0.5 cht-rounded-md ${
                                  item.delivery_mode === "online"
                                    ? "cht-bg-blue-50 cht-text-blue-700"
                                    : "cht-bg-emerald-50 cht-text-emerald-700"
                                }`}
                              >
                                {item.delivery_mode}
                              </span>
                            </div>
                            <p className="cht-text-[11px] cht-text-gray-400 cht-font-medium">
                              {item.student_details?.email}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Curriculum */}
                      <td className="cht-py-3 cht-px-6">
                        <div className="cht-flex cht-items-center cht-gap-2.5">
                          {item.curriculum?.image_url && (
                            <img
                              src={item.curriculum.image_url}
                              alt={item.curriculum.title}
                              className="cht-w-8 cht-h-8 cht-rounded-lg cht-object-cover cht-border cht-border-gray-200/60"
                            />
                          )}
                          <div className="cht-space-y-0.5">
                            <span className="cht-text-[9px] cht-font-black cht-text-[#3e57da] cht-uppercase">
                              {item.curriculum?.type === "course" ? "Course" : "Track"}
                            </span>
                            <p className="cht-font-bold cht-text-gray-800 cht-line-clamp-1">
                              {item.curriculum?.title}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Tuition & Financials */}
                      <td className="cht-py-3 cht-px-6">
                        <div className="cht-space-y-1">
                          <div className="cht-flex cht-items-center cht-gap-1.5">
                            <span
                              className={`cht-text-[9px] cht-font-black cht-uppercase cht-px-2 cht-py-0.5 cht-rounded-full ${
                                item.is_paid
                                  ? "cht-bg-emerald-50 cht-text-emerald-700"
                                  : "cht-bg-amber-50 cht-text-amber-700"
                              }`}
                            >
                              {item.is_paid ? "PAID" : "UNPAID"}
                            </span>
                            <span className="cht-font-black cht-text-gray-900">
                              ₦{Number(item.payment_amount || 0).toLocaleString()}
                            </span>
                          </div>
                          <p className="cht-text-[10px] cht-text-gray-400">
                            {item.payment_method || "Direct Intake"}
                          </p>
                        </div>
                      </td>

                      {/* Academic Progression */}
                      <td className="cht-py-3 cht-px-6">
                        <div className="cht-space-y-1.5 cht-w-36">
                          <div className="cht-flex cht-items-center cht-justify-between">
                            <span className="cht-text-[10px] cht-font-black cht-text-gray-700">
                              {item.progress_percent}%
                            </span>
                            {item.is_completed && (
                              <span className="cht-text-[9px] cht-font-black cht-text-purple-600 cht-flex cht-items-center cht-gap-0.5">
                                <Award size={10} /> Graduated
                              </span>
                            )}
                          </div>
                          <div className="cht-w-full cht-h-1.5 cht-bg-gray-100 cht-rounded-full cht-overflow-hidden">
                            <div
                              style={{ width: `${item.progress_percent}%` }}
                              className={`cht-h-full cht-rounded-full ${
                                item.is_completed
                                  ? "cht-bg-purple-600"
                                  : item.progress_percent > 50
                                  ? "cht-bg-[#3e57da]"
                                  : "cht-bg-amber-500"
                              }`}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Access State */}
                      <td className="cht-py-3 cht-px-6">
                        <span
                          className={`cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-black ${
                            item.is_enrolled
                              ? "cht-bg-emerald-50 cht-text-emerald-700"
                              : "cht-bg-rose-50 cht-text-rose-700"
                          }`}
                        >
                          {item.is_enrolled ? (
                            <>
                              <Unlock size={11} /> Active
                            </>
                          ) : (
                            <>
                              <Lock size={11} /> Suspended
                            </>
                          )}
                        </span>
                      </td>

                      {/* Actions Menu */}
                      <td className="cht-py-3 cht-px-6 cht-text-right cht-relative">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveDropdown(activeDropdown === item.id ? null : item.id);
                          }}
                          className="cht-p-1.5 cht-text-gray-400 hover:cht-text-gray-600 cht-rounded-lg hover:cht-bg-gray-100 cht-transition-colors"
                        >
                          <MoreVertical size={16} />
                        </button>

                        {/* Dropdown Menu */}
                        {activeDropdown === item.id && (
                          <div
                            ref={dropdownRef}
                            className={`cht-absolute cht-right-6 ${
                              isNearBottom ? "cht-bottom-full cht-mb-2" : "cht-top-full cht-mt-1"
                            } cht-w-56 cht-bg-white cht-rounded-2xl cht-shadow-2xl cht-border cht-border-gray-100 cht-py-2 cht-z-50 cht-text-left cht-animate-in cht-fade-in cht-zoom-in-95`}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setDetailModalItem(item);
                                setActiveDropdown(null);
                              }}
                              className="cht-w-full cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-flex cht-items-center cht-gap-2.5"
                            >
                              <Eye size={14} className="cht-text-blue-500" /> View Student Dossier
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setTuitionModalItem(item);
                                setActiveDropdown(null);
                              }}
                              className="cht-w-full cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-flex cht-items-center cht-gap-2.5"
                            >
                              <CreditCard size={14} className="cht-text-emerald-500" /> Settle Tuition
                            </button>

                            <button
                              type="button"
                              onClick={() => handleToggleAccess(item)}
                              className="cht-w-full cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-flex cht-items-center cht-gap-2.5"
                            >
                              {item.is_enrolled ? (
                                <>
                                  <Lock size={14} className="cht-text-amber-500" /> Suspend Access
                                </>
                              ) : (
                                <>
                                  <Unlock size={14} className="cht-text-emerald-500" /> Grant Access
                                </>
                              )}
                            </button>

                            <button
                              type="button"
                              onClick={() => handleToggleGraduation(item)}
                              className="cht-w-full cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-flex cht-items-center cht-gap-2.5"
                            >
                              <Award size={14} className="cht-text-purple-500" />
                              {item.is_completed ? "Reset Progress" : "Mark as Graduated"}
                            </button>

                            <div className="cht-my-1.5 cht-border-t cht-border-gray-100" />

                            <button
                              type="button"
                              onClick={() => handleDeleteEnrollment(item)}
                              className="cht-w-full cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-rose-600 hover:cht-bg-rose-50 cht-flex cht-items-center cht-gap-2.5"
                            >
                              <Trash2 size={14} /> Remove Enrollment
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Modals */}
      <ManualEnrollmentModal
        isOpen={isManualModalOpen}
        onClose={() => setIsManualModalOpen(false)}
        onSuccess={() => refetch()}
      />

      <ConfirmTuitionModal
        isOpen={Boolean(tuitionModalItem)}
        onClose={() => setTuitionModalItem(null)}
        enrollment={tuitionModalItem}
        onSuccess={() => refetch()}
      />

      <EnrollmentDetailModal
        isOpen={Boolean(detailModalItem)}
        onClose={() => setDetailModalItem(null)}
        enrollment={detailModalItem}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default SuperAdminEnrollments;
